/**
 * 🌑 AuraUpgradePanel — live aura upgrade loop (Roc #43, Sep 2026).
 *
 * Shows the user's currently-equipped Aura (via /api/aura/state), its
 * current level + EXP progression, how many Aura Pieces they're sitting
 * on, and a Consume button that feeds pieces into the equipped aura
 * (+50 EXP per piece).
 *
 * Endpoints:
 *   GET  /api/aura/state
 *   POST /api/aura/consume  { qty }
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuraUpgradePanel() {
  const [state, setState] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/aura/state`, { withCredentials: true });
      setState(r.data);
    } catch (e) {
      // Silent — panel just stays empty if the endpoint isn't reachable.
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const consume = async () => {
    if (!state?.equipped_aura?.upgradeable) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/aura/consume`, { qty },
        { withCredentials: true },
      );
      if (r.data?.leveled_up) {
        toast.success(`🌑 LEVEL UP! ${state.equipped_aura.name} is now Lv ${r.data.new_level}`,
          { duration: 5000 });
      } else {
        toast.success(`✨ +${r.data.exp_gained} EXP to ${state.equipped_aura.name}`,
          { duration: 3500 });
      }
      setState(r.data.state);
      setQty(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't absorb the piece.");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const aura = state.equipped_aura;
  const pieces = state.aura_pieces_owned || 0;

  // Empty aura slot
  if (!aura) {
    return (
      <div className="rounded-xl border border-neutral-800 bg-black/40 p-4"
           data-testid="aura-upgrade-panel-empty">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">🌑</span>
            <div>
              <div className="text-sm font-black text-amber-200 tracking-wide">AURA UPGRADE</div>
              <div className="text-[11px] text-neutral-500">
                Equip an Aura in your <b>Aura</b> slot to start leveling it with Aura Pieces.
              </div>
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-neutral-600 px-2 py-1 rounded border border-neutral-900">
            Not Equipped
          </span>
        </div>
      </div>
    );
  }

  // Preview-only aura (not yet usable)
  if (!aura.upgradeable) {
    return (
      <div className="rounded-xl border border-neutral-800 bg-black/40 p-4"
           data-testid="aura-upgrade-panel-preview">
        <div className="flex items-center gap-2">
          <span className="text-xl">{aura.icon || "🌈"}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-black text-amber-200 tracking-wide">{aura.name}</div>
            <div className="text-[11px] text-neutral-500">
              Preview-only — combat resolvers for this aura haven't shipped yet.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const barPct = aura.is_max
    ? 100
    : Math.min(100, Math.round((aura.exp_into_level / Math.max(1, aura.exp_this_level)) * 100));
  const maxConsumable = Math.min(
    pieces,
    aura.is_max ? 0 : Math.ceil((aura.exp_to_next || 50) / state.exp_per_piece),
  );
  const canConsume = pieces > 0 && !aura.is_max && !busy;

  return (
    <div className="rounded-xl border border-amber-700/40 bg-gradient-to-br from-black/60 via-[#1a1000]/40 to-black/40 p-4"
         data-testid="aura-upgrade-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-2xl shrink-0">{aura.icon}</span>
          <div className="min-w-0">
            <div className="text-sm font-black text-amber-200 tracking-wide truncate"
                 data-testid="aura-upgrade-name">{aura.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              Aura · {aura.rarity}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9px] uppercase tracking-widest text-amber-400/60">Level</div>
          <div className="text-xl font-black text-amber-300" data-testid="aura-upgrade-level">
            {aura.level}
            <span className="text-xs text-neutral-500">/{aura.max_level}</span>
          </div>
        </div>
      </div>

      {/* EXP bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] text-neutral-400 mb-1">
          <span>
            {aura.is_max ? (
              <b className="text-amber-300">MAX LEVEL</b>
            ) : (
              <>EXP to Lv {aura.level + 1}</>
            )}
          </span>
          <span className="font-mono text-neutral-500 tabular-nums"
                data-testid="aura-upgrade-exp-text">
            {aura.exp_into_level} / {aura.exp_this_level || "—"} ({aura.exp_total} total)
          </span>
        </div>
        <div className="h-2 rounded-full bg-black/70 border border-amber-900/40 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-600 via-amber-400 to-amber-300 transition-all"
               style={{ width: `${barPct}%` }}
               data-testid="aura-upgrade-exp-bar" />
        </div>
      </div>

      {/* Consume controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] text-neutral-400">
          You have <b className="text-amber-300" data-testid="aura-pieces-owned">
            {pieces}
          </b> ✨ Aura Piece{pieces === 1 ? "" : "s"} (×{state.exp_per_piece} EXP each)
        </div>
        <div className="flex items-center gap-2">
          <button type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  disabled={!canConsume || qty <= 1}
                  className="w-7 h-7 rounded border border-neutral-800 bg-black/60 text-neutral-300 hover:bg-black disabled:opacity-30"
                  data-testid="aura-qty-minus">−</button>
          <span className="font-mono tabular-nums text-amber-200 min-w-[2rem] text-center"
                data-testid="aura-qty">{qty}</span>
          <button type="button"
                  onClick={() => setQty((q) => Math.min(maxConsumable || 1, q + 1))}
                  disabled={!canConsume || qty >= maxConsumable}
                  className="w-7 h-7 rounded border border-neutral-800 bg-black/60 text-neutral-300 hover:bg-black disabled:opacity-30"
                  data-testid="aura-qty-plus">+</button>
          <button type="button"
                  onClick={consume}
                  disabled={!canConsume || qty < 1}
                  className="ml-2 text-[11px] font-black uppercase tracking-widest px-3 py-1.5 bg-amber-700 hover:bg-amber-600 text-white rounded disabled:opacity-40 transition"
                  data-testid="aura-consume-btn">
            {aura.is_max ? "Max Lv" : pieces === 0 ? "No Pieces" : `Consume ×${qty}`}
          </button>
        </div>
      </div>
    </div>
  );
}
